import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, Col, Row } from "react-bootstrap";
import EditableSectionModal from "../../proposals/proposalModals/pditableSectionModal";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { getDomainConfig, refreshDomainConfig } from "../../../config/domainConfig";

const TemplateRendererSection = ({
  sections = [],
  templateData = {},
  onContentChange,
  onOrderChange,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const [borderVisible, setBorderVisible] = useState(true);
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [editorValue, setEditorValue] = useState("");

  const [values, setValues] = useState({});
  const [draggableOrder, setDraggableOrder] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);

  const [finLabels, setFinLabels] = useState(getDomainConfig().financialLabels);

  useEffect(() => {
    const applyConfig = async () => {
      await refreshDomainConfig();
      setFinLabels(getDomainConfig().financialLabels);
    };
    applyConfig();
  }, []);

  useEffect(() => {
    if (Array.isArray(sections) && sections.length > 0) {
      //  Determine selected language
      const selectedLanguage =
        templateData?.selectedLanguage ||
        templateData?.LanguageCode ||
        localStorage.getItem("culture") ||
        "nl";

      //  Filter sections by selected language
      const filteredSections = sections.filter(
        (sec) =>
          !sec.LanguageCode ||
          sec.LanguageCode.toLowerCase() === selectedLanguage.toLowerCase()
      );

      const hasSavedOrder = filteredSections.some(
        (sec) => sec.SectionOrder !== undefined && sec.SectionOrder !== null
      );

      const orderedSections = hasSavedOrder
        ? [...filteredSections].sort((a, b) => a.SectionOrder - b.SectionOrder)
        : filteredSections;

      //  Map to hold section key -> content
      const sectionMap = new Map();

      orderedSections.forEach((sec) => {
        const key = sec.TemplateSection?.Key || sec.Key;
        if (key) {
          sectionMap.set(key, sec.DefaultContent || "");
        }
      });

      //  Prepare values and section order
      const initialValues = {};
      const sectionOrder = [];

      sectionMap.forEach((content, key) => {
        sectionOrder.push(key);
        // if section has no content, create placeholder
        initialValues[key] =
          content && content.trim() !== ""
            ? content
            : `<div class="placeholder-text">${t("noDefaultContentAvailable", {
              section: t(sectionTranlations[key] || key),
            })}</div>`;

      });

      let finalOrder = sectionOrder;
      if (!hasSavedOrder) {
        const staticSections = ["introTextBlock", "timeScheduleText", "proposalTasksBlock"];
        finalOrder = [
          ...staticSections.filter((s) => sectionOrder.includes(s)),
          ...sectionOrder.filter((s) => !staticSections.includes(s))
        ];
      }

      setValues(initialValues);
      setDraggableOrder(finalOrder);
    } else {
      //  Fallback for empty/new templates
      setValues({
        priceSection: `<div class="placeholder-text">${t("priceSectionExample")}</div>`,
        paymentTermsSection: `<div class="placeholder-text">${t("paymentTermsSectionExample")}</div>`,
        acceptanceSection: `<div class="placeholder-text">${t("acceptanceSectionExample")}</div>`,
        extraContentText: `<div class="placeholder-text">${t("extraContentSectionExample")}</div>`,
        terminationSection: `<div class="placeholder-text">${t("terminationSectionExample")}</div>`
      });

      setDraggableOrder([
        "introTextBlock",
        "timeScheduleText",
        "proposalTasksBlock",
        "priceSection",
        "paymentTermsSection",
        "acceptanceSection",
        "extraContentText",
        "terminationSection"
      ]);
    }

    //  Hide borders in read-only mode
    if (readOnly) setBorderVisible(false);
  }, [sections, readOnly, templateData, t]);

  const handleDragStart = (e, key) => {
    setDraggedItem(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    if (draggedItem !== key) {
      setDraggedOverItem(key);
    }
  };

  const handleDragEnd = () => {
    if (draggedItem && draggedOverItem && draggedItem !== draggedOverItem) {
      const fromIndex = draggableOrder.indexOf(draggedItem);
      const toIndex = draggableOrder.indexOf(draggedOverItem);

      const newOrder = [...draggableOrder];
      const [removed] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, removed);

      setDraggableOrder(newOrder);
    }
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const handleRemoveSection = (key) => {
    setDraggableOrder((prev) => prev.filter((item) => item !== key));
  };

  const renderLine = (text) => (text ? <>{text}<br /></> : null);

  const data = {
    logo: t("dataPlaceholders.logo"),
    companyName: t("dataPlaceholders.companyNamePlaceholders"),
    contactPerson: t("dataPlaceholders.contactPersonPlaceholders"),
    address: t("dataPlaceholders.addressPlaceholders"),
    city: t("dataPlaceholders.cityPlaceholders"),
    email: t("dataPlaceholders.emailPlaceholders"),
    clientName: t("dataPlaceholders.organizationNamePlaceholders"),
    clientAddress: t("dataPlaceholders.clientAddressPlaceholders"),
    clientCity: t("dataPlaceholders.clientCityPlaceholders"),
    clientPhone: t("dataPlaceholders.clientPhonePlaceholders"),
    clientEmail: t("dataPlaceholders.clientEmailPlaceholders"),
    clientWebsite: t("dataPlaceholders.clientWebsitePlaceholders"),
    date: t("dataPlaceholders.datePlaceholdersPlaceholders"),
    startDate: t("dataPlaceholders.startDatePlaceholders"),
    endDate: t("dataPlaceholders.endDatePlaceholders"),
    startTime: t("dataPlaceholders.startTimePlaceholders"),
    price: t("dataPlaceholders.pricePlaceholders"),
    iban: t("dataPlaceholders.ibanPlaceholders"),
    gAccount: t("dataPlaceholders.gAccountPlaceholders"),
    chamberOfCommerce: t("dataPlaceholders.chamberOfCommercePlaceholders"),
    vatNumber: t("dataPlaceholders.vatNumberPlaceholders"),
  };

  const handleOpenEditor = (key) => {
    setEditingKey(key);
    setEditorValue(values[key] || "");
    setEditorVisible(true);
  };

  const handleCloseEditor = () => {
    setEditorVisible(false);
    setEditingKey("");
  };

  const handleSaveEditor = (updatedValue) => {
    setValues((prev) => ({
      ...prev,
      [editingKey]: updatedValue,
    }));
    handleCloseEditor();
  };
  
  const sectionTranlations = {
    priceSection: "price",
    paymentTermsSection: "payment",
    acceptanceSection: "acceptance",
    extraContentText: "extraContentText",
    terminationSection: "terminationSection",
    introTextBlock: "introTextBlockLable"
  };

  const renderEditableSection = (key) => {
    const isEmpty = !values[key] || values[key].replace(/<[^>]*>/g, "").trim() === "";
    if (!borderVisible && isEmpty) return null;

    const isDragging = draggedItem === key;
    const isDraggedOver = draggedOverItem === key;

    return (
      <div
        key={key}
        className={`mb-3 ${isDragging ? "dragging" : ""}`}
        draggable={borderVisible && !readOnly}
        onDragStart={(e) => handleDragStart(e, key)}
        onDragOver={(e) => handleDragOver(e, key)}
        onDragEnd={handleDragEnd}
        style={{ opacity: isDragging ? 0.5 : 1 }}
      >
        <div
          className={`commonStyles fs-10 editableSection cursor-pointer ${borderVisible ? "borderVisible" : ""} ${isDraggedOver && borderVisible ? "draggedOver" : ""
            }`}
          onClick={(e) => {
            if (readOnly) return;
            e.stopPropagation();
            handleOpenEditor(key);
          }}
        >
          {borderVisible && !readOnly && (
            <div className="pencilIconContainer editableIcons">
              <i
                className="fas fa-grip-lines pencilIcon gripIcon cursor-pointer"
                title={t("titleDragToReorder")}
                onClick={(e) => e.stopPropagation()}
              />
              <i
                className="fas fa-pencil-alt pencilIcon editIcon cursor-pointer"
                title={t("titleEditSection")}
                onClick={(e) => {
                  if (readOnly) return;
                  e.stopPropagation();
                  handleOpenEditor(key);
                }}
              />
              <i
                className="fas fa-times pencilIcon removeIcon cursor-pointer"
                title={t("removeSection")}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveSection(key);
                }}
              />
            </div>
          )}

          <div>
            <h6>{t(sectionTranlations[key] || key)}</h6>
            <div
              id={key}
              contentEditable={false}
              suppressContentEditableWarning={true}
              dangerouslySetInnerHTML={{
                __html: !isEmpty
                  ? values[key]
                  : borderVisible
                    ? `<div class="placeholder-text">${t("noDefaultContent", {
                      section: t(sectionTranlations[key] || key)
                    })}</div>`
                    : "",
              }}
            />
          </div>

        </div>
      </div>
    );
  };

  const renderStaticSection = (key) => {
    const isDragging = draggedItem === key;
    const isDraggedOver = draggedOverItem === key;

    // Section titles for read-only mode
    const readonlyTitles = {
      introTextBlock: t("introSection") || "Introduction",
      timeScheduleText: t("timeScheduleDescription") || "Time Schedule",
      proposalTasksBlock:
        t("tasksOverviewText") ||
        "Work program for window cleaning activities",
    };

    return (
      <div
        key={key}
        className={`mb-3 ${isDragging ? "dragging" : ""}`}
        draggable={borderVisible && !readOnly}
        onDragStart={(e) => handleDragStart(e, key)}
        onDragOver={(e) => handleDragOver(e, key)}
        onDragEnd={handleDragEnd}
        style={{ opacity: isDragging ? 0.5 : 1 }}
      >
        <div
          className={`commonStyles fs-10 ${borderVisible ? "borderVisible" : "borderHidden"
            } ${isDraggedOver && borderVisible ? "draggedOver" : ""} ${readOnly ? "readonly-mode" : "grayedOutSection"
            }`}
        >
          <h6 >{t(sectionTranlations[key] || key)}</h6>
          {/* Show icons only in edit/create mode */}
          {borderVisible && !readOnly && (
            <div className="pencilIconContainer spacedIcons placeholder-text">
              <i
                className="fas fa-grip-lines pencilIcon gripIcon"
                style={{ cursor: "move" }}
                title={t("titleDragToReorder")}
              />
              <i
                className="fas fa-times pencilIcon removeIcon cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveSection(key);
                }}
                title={t("removeSection")}
              />
            </div>
          )}

          <div className="static-content-wrapper">
            {!readOnly && (
              <div className="placeholder-text mb-2">
                {t("nonEditableSectionNotice") || "This section is not editable"}
              </div>
            )}

            {key === "introTextBlock" && (
              <>
                {!readOnly && (
                  <div className="placeholder-text mb-1">{readonlyTitles.introTextBlock}</div>
                )}
                <p className={!readOnly ? "mb-0 placeholder-text" : "mb-0"}>
                  {t("introExample")}
                </p>
              </>
            )}

            {/* === Time Schedule Section === */}
            {key === "timeScheduleText" && (
              <>
                {!readOnly && (
                  <div className="placeholder-text mb-1">{readonlyTitles.timeScheduleText}</div>
                )}
                <p className={!readOnly ? "mb-0 placeholder-text" : "mb-0"}>
                  {t("timeScheduleExample")}
                </p>
              </>
            )}

            {/* === Proposal Tasks Section === */}
            {key === "proposalTasksBlock" && (
              <>
                <div className="placeholder-text mb-1">{readonlyTitles.proposalTasksBlock}</div>
               <div className="placeholder-text mb-1">{t("forExample")} :</div>
                <ul className={!readOnly ? "mb-1 placeholder-text" : "mb-1"}>
                  <li>
                    {t("glassCleaningTitle")}
                    <ul className={!readOnly ? "mb-1 placeholder-text" : "mb-1"}>
                      <li>{t("glassCleaningHand")}</li>
                      <li>{t("glassCleaningTelescopic")}</li>
                      <li>{t("glassCleaningInterior")}</li>
                    </ul>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (key) => {
    if (
      [
        "priceSection",
        "paymentTermsSection",
        "acceptanceSection",
        "extraContentText",
        "terminationSection",
      ].includes(key)
    ) {
      return renderEditableSection(key);
    }

    if (["introTextBlock", "timeScheduleText", "proposalTasksBlock"].includes(key)) {
      return renderStaticSection(key);
    }

    switch (key) {
      case "logoSection":
        return (
          <div className="template-header-row" key={key}>
            <div className="template-left-column">
              <div className="template-logo">{data.logo}</div>
              <div className="template-text">
                <strong>{data.companyName}</strong>
                <br />
                {renderLine(data.contactPerson)}
                {renderLine(data.address)}
                {renderLine(data.city)}
                {renderLine(data.email)}
              </div>
            </div>

            <div className="template-right-column">
              <div className="template-text">
                <strong>{data.clientName}</strong>
                <br />
                {renderLine(data.clientAddress)}
                {renderLine(data.clientCity)}
                {renderLine(data.clientPhone)}
                {renderLine(data.clientEmail)}
                {renderLine(data.clientWebsite)}
              </div>
            </div>
          </div>
        );

      case "dateSection":
        return (
          <div className="template-date-text" key={key}>
            {data.date}
          </div>
        );

      case "salutation":
        return (
          <div className="template-salutation" key={key}>
            {t("dearPlaceholders")} {data.contactPerson},
          </div>
        );

      case "signatureBlock":
        return (
          <div className="template-signature-block" key={key}>
            <p className="fs-10 mb-2">{t("signedCopyReturn")}</p>
            <p className="fs-10 mb-2">{t("greeting")}</p>
            <Row className="mt-3">
              <Col xs={12} md={6} className="text-left">
                <h6>{t("companySignature")}</h6>
                <div className="signature-footer">
                  <p className="mb-2">_______________________________</p>
                  <p className="mb-2">
                    <small>{t("signaturePlaceholder")}</small>
                  </p>
                </div>
                <p className="fs-10 mt-2 mb-0">
                  {t("dataPlaceholders.adminName")},
                  <br />
                  {t("dataPlaceholders.organizationNamePlaceholders")}
                  <br />
                  {t("dataPlaceholders.cityPlaceholders")}
                </p>
              </Col>

              <Col xs={12} md={6} className="text-left">
                <h6>{t("customerSignature")}</h6>
                <div className="signature-footer">
                  <p className="mb-2">_______________________________</p>
                  <p className="mb-2">
                    <small>{t("signaturePlaceholder")}</small>
                  </p>
                </div>
                <p className="fs-10 mt-2 mb-0">
                  {t("dataPlaceholders.contactPersonPlaceholders")},
                  <br />
                  {t("dataPlaceholders.organizationNamePlaceholders")}
                  <br />
                  {t("dataPlaceholders.cityPlaceholders")}
                </p>
              </Col>
            </Row>

            <div className="template-text text-end mt-5">
              <p className="mb-0">
                {!finLabels.showIfsc && (
                  <>
                    {t("dataPlaceholders.ibanPlaceholders")}
                    <br />
                    {t("dataPlaceholders.gAccountPlaceholders")}
                    <br />
                    {t("dataPlaceholders.chamberOfCommercePlaceholders")}
                    <br />
                    {t("dataPlaceholders.vatNumberPlaceholders")}
                  </>
                )}
                {finLabels.showIfsc && (
                  <>
                    {t("dataPlaceholders.accountPlaceholders")}
                    <br />
                    {t("dataPlaceholders.ifscPlaceholders")}
                    <br />
                    {t("dataPlaceholders.gstPlaceholders")}
                  </>
                )}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getPagesFromSections = () => {
    const pages = [];
    let current = [];

    const allSections = ["logoSection", "dateSection", "salutation", ...draggableOrder, "signatureBlock"];

    allSections.forEach((key) => {
      current.push(key);
    });

    if (current.length > 0) pages.push(current);
    return pages;
  };

  useEffect(() => {
    if (onContentChange) {
      onContentChange(values);
    }
  }, [values]);

  useEffect(() => {
    if (onOrderChange) {
      onOrderChange(draggableOrder);
    }
  }, [draggableOrder]);

  return (
    <>
      <div>
        {getPagesFromSections().map((sectionKeys, pageIndex) => (
          <Card key={pageIndex} className="mb-3">
            <Card.Body>
              {sectionKeys.map((key, index) => (
                <React.Fragment key={index}>{renderSection(key)}</React.Fragment>
              ))}
            </Card.Body>
          </Card>
        ))}
      </div>

      <EditableSectionModal
        initialContent={editorValue}
        show={editorVisible}
        onClose={handleCloseEditor}
        onSave={handleSaveEditor}
        label= {t(sectionTranlations[editingKey])}
      />
    </>
  );
};

export default TemplateRendererSection;